import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faXmark } from '@fortawesome/free-solid-svg-icons';
import UnitMappingService from '../services/unitMappingService';
import http from '../services/httpClient';
import UnitChangeConfirmationModal from './UnitChangeConfirmationModal';
import CrossFamilyConversionModal from './CrossFamilyConversionModal';
import { isCrossFamilyChange, UNIT_ID_MAP } from '../utils/unitDisplay';
// Reuse the (global, non-module) Inventory Overview styles so the pencil menu +
// edit modal look identical to the Overview version.
import './InventoryOverview-module.css';

const EMPTY_FORM = {
  currentName: '',
  newName: '',
  mergeTargetName: '',
  currentUnit: '',
  newUnit: '',
  conversionRate: '',
};

const UNIT_OPTIONS = [
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'kg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'L' },
  { value: 'pcs', label: 'pcs' },
];

function normalizeInventoryName(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * "Merged X into Y" is true but incomplete when a saved preparation moved with the merge — that
 * is the part that changes what future sales deduct, and the user has no other way to find out
 * it happened.
 */
function mergeSuccessMessage(sourceName, targetName, result) {
  const base = `Merged "${sourceName}" into "${targetName}".`;
  const prep = result?.preparations;
  if (!prep) return base;
  const claimed = prep.linkTransferred || prep.adoptedByName;
  const parts = [];
  if (claimed) parts.push(`"${targetName}" is now made from "${claimed.prepName}"`);
  else if (prep.inheritedPreparation) parts.push(`"${targetName}" is made from "${prep.inheritedPreparation.prepName}"`);
  if (prep.componentsFolded > 0) {
    parts.push(`${prep.componentsFolded} preparation${prep.componentsFolded === 1 ? '' : 's'} had both items — their quantities were added together`);
  }
  return parts.length > 0 ? `${base} ${parts.join('; ')}.` : base;
}

/**
 * InventoryItemEditMenu
 * =====================
 * Self-contained port of the Inventory Overview "pencil" edit feature:
 *   - a pencil button that opens a 3-option menu (Change item name / Merge with
 *     other item / Change units)
 *   - a modal with the mode-specific form + inventory-name autocomplete
 *   - a confirm step showing the related recipes/preparations that will be affected
 *   - rename  -> PUT  /inventory/items/rename
 *   - merge   -> POST /recipes/merge-items
 *   - unit    -> same-family: previewUnitChange -> UnitChangeConfirmationModal -> confirmUnitChange
 *                cross-family: CrossFamilyConversionModal (preview/commit conversion)
 *
 * Props:
 *   businessId?     - defaults to UnitMappingService.getBusinessId()
 *   preferredNames? - names to float to the top of the autocomplete (e.g. the current recipe's ingredients)
 *   onChanged?      - called after a successful rename/merge/unit change (parent should refresh)
 *   onNotify?       - (message, type) => void  toast callback
 *   buttonStyle?    - inline style for the pencil button (to match the host toolbar)
 *   buttonClassName?- className for the pencil button
 *   buttonTitle?
 */
const InventoryItemEditMenu = ({
  businessId: businessIdProp,
  preferredNames = [],
  onChanged,
  onNotify,
  buttonStyle,
  buttonClassName,
  buttonTitle = 'Edit inventory items',
  externalTrigger = null,
  onTriggerClear = null,
}) => {
  const businessId = businessIdProp || UnitMappingService.getBusinessId();
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;

  const [items, setItems] = useState([]); // { id, name, unit, standard_unit_id }
  const [preps, setPreps] = useState([]); // { id, name, unit }  (saved preparations)
  const [selectedPrep, setSelectedPrep] = useState(null); // set when the user picks a preparation
  const [showMenu, setShowMenu] = useState(false);
  const menuWrapRef = useRef(null);
  const triggeredExternallyRef = useRef(false);

  const [mode, setMode] = useState(null); // 'rename' | 'merge' | 'unit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  const [showNameSug, setShowNameSug] = useState(false);
  const [showMergeSug, setShowMergeSug] = useState(false);
  const [showUnitDrop, setShowUnitDrop] = useState(false);
  const [showCurrentUnitDrop, setShowCurrentUnitDrop] = useState(false);

  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const usageKeyRef = useRef('');

  // Unit-change confirmation + cross-family modal state
  const [showUnitConfirm, setShowUnitConfirm] = useState(false);
  const [unitPreview, setUnitPreview] = useState(null);
  const [pendingUnit, setPendingUnit] = useState(null);
  const [applyingUnit, setApplyingUnit] = useState(false);
  const [showCrossFamily, setShowCrossFamily] = useState(false);
  const [crossFamilyData, setCrossFamilyData] = useState(null);
  // Set when the user picked a SAVED PREPARATION as the merge target rather than an item. The
  // two share a field and can share a name, so which row was clicked has to be remembered.
  const [mergeTargetPrep, setMergeTargetPrep] = useState(null);

  const notify = useCallback((msg, type = 'success') => {
    if (typeof onNotify === 'function') onNotify(msg, type);
  }, [onNotify]);

  // ── Load inventory items (for autocomplete + unit/id resolution) ──────────
  const loadItems = useCallback(async () => {
    if (!businessId) return;
    try {
      const data = await UnitMappingService.getInventoryItems(businessId);
      const list = (Array.isArray(data) ? data : [])
        .map((it) => ({
          id: it.id || it.item_id,
          name: String(it.name || '').trim(),
          unit: String(it.unit_symbol || it.standardUnit || it.standard_unit || '').trim(),
          standard_unit_id: it.standard_unit_id || null,
        }))
        .filter((x) => x.id && x.name);
      setItems(list);
    } catch (e) {
      // Non-fatal: autocomplete just stays empty.
      console.warn('InventoryItemEditMenu: failed to load inventory items', e?.message);
    }
    // Saved preparations (offered in the "Change units" picker only)
    try {
      const pr = await http.get(`/medium-features/decomposition/preparations?businessId=${businessId}`);
      const plist = (pr && pr.success && Array.isArray(pr.data) ? pr.data : [])
        .map((p) => ({ id: p.id, name: String(p.name || '').trim(), unit: String(p.unit_symbol || p.unit_name || '').trim() }))
        .filter((x) => x.id && x.name);
      setPreps(plist);
    } catch (e) {
      console.warn('InventoryItemEditMenu: failed to load preparations', e?.message);
    }
  }, [businessId]);

  useEffect(() => {
    if (showMenu || mode) loadItems();
  }, [showMenu, mode, loadItems]);

  // Close the menu on outside click
  useEffect(() => {
    if (!showMenu) return undefined;
    const onDoc = (e) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showMenu]);

  const findItemByName = useCallback((name) => {
    const key = normalizeInventoryName(name);
    if (!key) return null;
    return items.find((it) => normalizeInventoryName(it.name) === key) || null;
  }, [items]);

  const findPrepByName = useCallback((name) => {
    const key = normalizeInventoryName(name);
    if (!key) return null;
    return preps.find((p) => normalizeInventoryName(p.name) === key) || null;
  }, [preps]);

  const nameOptions = useMemo(() => {
    const seen = new Set();
    const preferred = [];
    const rest = [];
    const preferKeys = new Set(preferredNames.map(normalizeInventoryName));
    items.forEach((it) => {
      const key = normalizeInventoryName(it.name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      (preferKeys.has(key) ? preferred : rest).push(it.name);
    });
    rest.sort((a, b) => a.localeCompare(b));
    return [...preferred, ...rest];
  }, [items, preferredNames]);

  // Suggestions for the "Current item name" field. Items always; preparations are added
  // ONLY in "Change units" mode (tagged, so a same-named item vs preparation is distinguishable).
  const nameSuggestions = useMemo(() => {
    const itemEntries = nameOptions.map((n) => ({ name: n, label: n, prep: null }));
    const entries = mode === 'unit'
      ? [...itemEntries, ...preps.map((p) => ({ name: p.name, label: `${p.name} — Preparation`, prep: p }))]
      : itemEntries;
    const q = normalizeInventoryName(form.currentName);
    const filtered = q ? entries.filter((e) => normalizeInventoryName(e.name).includes(q)) : entries;
    return filtered.slice(0, 10);
  }, [form.currentName, nameOptions, preps, mode]);

  /**
   * What "Merge into" may point at — items AND saved preparations.
   *
   * Items only was the whole problem. "Garam Masala Indian" is a preparation, not an inventory
   * item, so it could never appear here however carefully it was typed: filtering out the
   * current item left "gara" matching nothing at all, and the field looked broken. But merging
   * an ingredient into a preparation is exactly what a user means by it — the preparation should
   * take over what that ingredient does — and the app already has the operation for it.
   *
   * So preparations are offered, tagged, and picking one runs Convert item → preparation instead
   * of a merge. Same intent, and the right machinery underneath: nothing is deleted, the recipes
   * keep pointing where they point, and one id binds the two.
   */
  const mergeSuggestions = useMemo(() => {
    const currentKey = normalizeInventoryName(form.currentName);
    const itemEntries = nameOptions
      .filter((n) => normalizeInventoryName(n) !== currentKey)
      .map((n) => ({ name: n, label: n, prep: null }));
    // A preparation sharing the current item's name is kept: converting "Garam Masala" the item
    // into "Garam Masala" the preparation is a legitimate, common pairing.
    const prepEntries = preps.map((p) => ({ name: p.name, label: `${p.name} — Preparation`, prep: p }));
    const entries = [...itemEntries, ...prepEntries];

    const q = normalizeInventoryName(form.mergeTargetName);
    const filtered = q ? entries.filter((e) => normalizeInventoryName(e.name).includes(q)) : entries;
    return filtered.slice(0, 10);
  }, [form.mergeTargetName, form.currentName, nameOptions, preps]);

  const currentUnitOptions = useMemo(() => {
    const currentRaw = String(form.currentUnit || '').trim();
    const currentNorm = UnitMappingService.normalizeUnitSymbol(currentRaw) || currentRaw;
    const family = UnitMappingService.getUnitFamily(currentNorm);
    
    if (family === 'weight') {
      return [
        { value: 'kg', label: 'kg' },
        { value: 'g', label: 'g' }
      ];
    }
    if (family === 'volume') {
      return [
        { value: 'L', label: 'L' },
        { value: 'ml', label: 'ml' }
      ];
    }
    if (family === 'count') {
      return [
        { value: 'pcs', label: 'pcs' }
      ];
    }
    return currentRaw ? [{ value: currentRaw, label: currentRaw }] : [];
  }, [form.currentUnit]);

  const filteredUnitOptions = useMemo(() => {
    const currentRaw = String(form.currentUnit || '').trim();
    const current = (UnitMappingService?.normalizeUnitSymbol?.(currentRaw) || currentRaw).toLowerCase();
    if (current === 'kg' || current === 'g') {
      return [
        { value: 'ml', label: 'ml' },
        { value: 'L', label: 'L' },
        { value: 'pcs', label: 'pcs' }
      ];
    }
    if (current === 'l' || current === 'ml') {
      return [
        { value: 'g', label: 'g' },
        { value: 'kg', label: 'kg' },
        { value: 'pcs', label: 'pcs' }
      ];
    }
    return UNIT_OPTIONS.filter((u) => u.value.toLowerCase() !== current);
  }, [form.currentUnit]);

  // ── Open / close ──────────────────────────────────────────────────────────
  const openModal = useCallback((nextMode, initialForm = EMPTY_FORM) => {
    setMode(nextMode);
    setForm(initialForm);
    setSelectedPrep(null);
    setError('');
    setLoading(false);
    setConfirmStep(false);
    setUsage(null);
    setUsageError('');
    usageKeyRef.current = '';
    setShowMenu(false);
  }, []);

  useEffect(() => {
    if (externalTrigger) {
      const { name, mode: triggerMode } = externalTrigger;
      if (triggerMode) {
        triggeredExternallyRef.current = true;
        openModal(triggerMode, {
          ...EMPTY_FORM,
          currentName: name || '',
        });
      }
      if (onTriggerClear) {
        onTriggerClear();
      }
    }
  }, [externalTrigger, openModal, onTriggerClear]);

  // Auto-pick matched item/prep for external triggers when items/preps are loaded
  useEffect(() => {
    if (triggeredExternallyRef.current && mode && form.currentName && (items.length > 0 || preps.length > 0)) {
      const q = normalizeInventoryName(form.currentName);
      const matchedPrep = preps.find((p) => normalizeInventoryName(p.name) === q);
      const matchedItem = items.find((it) => normalizeInventoryName(it.name) === q);

      if (matchedPrep || matchedItem) {
        setSelectedPrep(matchedPrep || null);
        setForm((prev) => ({
          ...prev,
          currentUnit: matchedPrep 
            ? String(matchedPrep.unit || '').trim() 
            : (matchedItem ? String(matchedItem.unit || '').trim() : ''),
          newUnit: ''
        }));
        setShowNameSug(false);
        triggeredExternallyRef.current = false; // completed
      }
    }
  }, [mode, form.currentName, items, preps]);

  const closeModal = useCallback(() => {
    setMode(null);
    setForm(EMPTY_FORM);
    setSelectedPrep(null);
    setError('');
    setLoading(false);
    setConfirmStep(false);
    setUsage(null);
    setUsageError('');
    usageKeyRef.current = '';
    setShowNameSug(false);
    setShowMergeSug(false);
    setShowUnitDrop(false);
    setShowCurrentUnitDrop(false);
    setMergeTargetPrep(null);
  }, []);

  const setField = useCallback((field, value) => {
    if (field === 'currentName') setSelectedPrep(null); // typing clears any explicit preparation pick
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'currentName' && mode === 'unit') {
        // Prefer an inventory item; fall back to a preparation (fills the unit either way).
        const item = findItemByName(value);
        const prep = item ? null : findPrepByName(value);
        const rawUnit = String((item || prep)?.unit || '').trim();
        next.currentUnit = UnitMappingService.normalizeUnitSymbol(rawUnit) || rawUnit;
      }
      return next;
    });
    if (confirmStep && ['currentName', 'newName', 'mergeTargetName', 'currentUnit', 'newUnit'].includes(field)) {
      setConfirmStep(false);
    }
    if (error) setError('');
  }, [mode, findItemByName, findPrepByName, confirmStep, error]);

  // Explicit selection from the suggestion list — carries whether it's an item or a preparation.
  const pickCurrentName = useCallback((entry) => {
    setSelectedPrep(entry.prep || null);
    setForm((prev) => {
      const next = { ...prev, currentName: entry.name };
      if (mode === 'unit') {
        if (entry.prep) {
          const rawUnit = String(entry.prep.unit || '').trim();
          next.currentUnit = UnitMappingService.normalizeUnitSymbol(rawUnit) || rawUnit;
        } else {
          const it = findItemByName(entry.name);
          const rawUnit = it ? String(it.unit || '').trim() : '';
          next.currentUnit = UnitMappingService.normalizeUnitSymbol(rawUnit) || rawUnit;
        }
      }
      return next;
    });
    setShowNameSug(false);
    setConfirmStep(false);
    if (error) setError('');
  }, [mode, findItemByName, error]);

  // ── Usage details (loaded while modal open, shown in confirm step) ─────────
  useEffect(() => {
    if (!mode || selectedPrep) return undefined; // preparations have no item-usage panel
    const currentName = form.currentName.trim();
    if (!currentName) {
      setUsage(null);
      setUsageLoading(false);
      setUsageError('');
      return undefined;
    }
    const match = findItemByName(currentName);
    if (!match) {
      setUsage(null);
      setUsageLoading(false);
      setUsageError('');
      usageKeyRef.current = '';
      return undefined;
    }
    const key = `${mode}:${match.id}:${currentName.toLowerCase()}`;
    if (usageKeyRef.current === key) return undefined;
    usageKeyRef.current = key;

    let cancelled = false;
    setUsageLoading(true);
    setUsageError('');
    http.get(`/inventory/items/${match.id}/usage`)
      .then((result) => {
        if (cancelled) return;
        if (!result?.success) throw new Error(result?.error || 'Failed to load related items.');
        setUsage({
          menuItems: Array.isArray(result.menuItems) ? result.menuItems : [],
          preparations: Array.isArray(result.preparations) ? result.preparations : [],
          complimentaryItems: Array.isArray(result.complimentaryItems) ? result.complimentaryItems : [],
        });
      })
      .catch(() => {
        if (cancelled) return;
        setUsage({ menuItems: [], preparations: [], complimentaryItems: [] });
        setUsageError('Could not load related items right now.');
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, selectedPrep, form.currentName, findItemByName]);

  // ── Submit (2-phase: first press => confirm step, second => execute) ───────
  const handleSubmit = useCallback(async () => {
    if (!mode) return;
    const currentName = form.currentName.trim();
    if (!currentName) {
      setError('Please enter the current item name.');
      return;
    }
    if (!confirmStep && mode !== 'unit') {
      setError('');
      setConfirmStep(true);
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (mode === 'rename') {
        const newName = form.newName.trim();
        if (!newName) { setError('Please enter the new item name.'); setLoading(false); return; }
        const result = await http.put('/inventory/items/rename', { currentName, newName });
        if (!result?.success) throw new Error(result?.error || 'Failed to rename item.');
        notify('Item renamed successfully.', 'success');
        closeModal();
        // Payload lets the host patch the rows it already has on screen, so the
        // change shows immediately instead of only after a reopen.
        if (onChanged) onChanged({ mode: 'rename', itemName: currentName, newName });
        return;
      }

      if (mode === 'merge') {
        const mergeTargetName = form.mergeTargetName.trim();
        if (!mergeTargetName) { setError('Please enter the item name to merge into.'); setLoading(false); return; }
        const source = findItemByName(currentName);
        if (!source) { setError('Current item not found in inventory.'); setLoading(false); return; }

        // ── Target is a saved preparation ─────────────────────────────────────
        // Not a merge at all: nothing is deleted and no recipe is re-pointed. The item stays as
        // the face every recipe already points at, and the preparation becomes what it is made
        // from, so a sale deducts the preparation's components scaled to the recipe's amount.
        // An explicitly clicked preparation wins; a typed name resolves to an item first,
        // because merging two items is the safer reading of an ambiguous name.
        const targetItem = findItemByName(mergeTargetName);
        const targetPrep = mergeTargetPrep || (targetItem ? null : findPrepByName(mergeTargetName));
        if (targetPrep) {
          // Leftover stock is written off as part of the change rather than asked about: "use it
          // up first" is not something anyone can do with 2 g of a spice. The write-off is in the
          // operation's snapshot, so reverting it from the activity log restores the stock.
          const result = await http.post('/medium-features/decomposition/convert', {
            itemId: source.id, prepId: targetPrep.id, businessId, settleStock: true,
          });
          if (!result?.success) {
            throw new Error(result?.error || 'Failed to use this preparation for the item.');
          }
          // The rename is named out loud: it changes what every other screen calls the
          // ingredient, and a rename nobody expected is worse than one they were told about.
          notify(
            result.data?.renamed
              ? `"${currentName}" is now "${targetPrep.name}", made from the preparation of that name.`
              : `"${currentName}" is now made from "${targetPrep.name}".`,
            'success'
          );
          closeModal();
          // The item itself does not change or disappear — only what it is made from — so the
          // host is told to refresh rather than to remove a row.
          if (onChanged) {
            onChanged({
              mode: 'convert',
              itemName: currentName,
              sourceName: currentName,
              targetName: targetPrep.name,
              targetUnit: source.unit || '',
            });
          }
          return;
        }

        const target = targetItem;
        if (!target) { setError('Target item not found in inventory.'); setLoading(false); return; }
        if (source.id === target.id) { setError('Please choose a different item to merge into.'); setLoading(false); return; }
        const su = UnitMappingService.normalizeUnitSymbol(source.unit);
        const tu = UnitMappingService.normalizeUnitSymbol(target.unit);
        // Allow same-family merges (kg↔g, L↔ml) — the backend converts the source's
        // stock/batches into the target's unit. Only block cross-family merges.
        if (su && tu && isCrossFamilyChange(su, tu)) {
          setError(`Items must be in the same unit family to merge (${su} vs ${tu}).`);
          setLoading(false);
          return;
        }
        const result = await http.post('/recipes/merge-items', {
          duplicate_item_id: source.id,
          master_item_id: target.id,
          businessId,
        });
        if (!result?.success) {
          throw new Error(result?.error || 'Failed to merge items.');
        }
        notify(mergeSuccessMessage(currentName, mergeTargetName, result), 'success');
        closeModal();
        if (onChanged) {
          onChanged({
            mode: 'merge',
            itemName: currentName,          // source (disappears)
            sourceName: currentName,
            targetName: target.name || mergeTargetName,
            targetUnit: target.unit || '',
          });
        }
        return;
      }

      if (mode === 'unit') {
        const currentUnit = form.currentUnit.trim();
        const newUnit = form.newUnit.trim();
        if (!currentUnit || !newUnit) { setError('Please select both current and new unit.'); setLoading(false); return; }
        const normCurrent = UnitMappingService.normalizeUnitSymbol(currentUnit) || currentUnit;
        const normNew = UnitMappingService.normalizeUnitSymbol(newUnit) || newUnit;
        if (normCurrent === normNew) { setError('Source and destination units are the same.'); setLoading(false); return; }

        // ── Preparation unit change (batch yield) ────────────────────────────
        // Use the prep flow when the user explicitly picked a preparation, or when the
        // name only matches a preparation (not an inventory item).
        const prepMatch = selectedPrep || (findItemByName(currentName) ? null : findPrepByName(currentName));
        if (prepMatch) {
          const crossFam = isCrossFamilyChange(normCurrent, normNew);
          const rateRaw = String(form.conversionRate).trim();
          const rate = rateRaw !== '' ? parseFloat(rateRaw) : undefined;
          if (crossFam && !(rate > 0)) {
            setError(`Weight↔volume change needs a rate (1 ${normCurrent} = ? ${normNew}).`);
            setLoading(false);
            return;
          }
          const result = await http.put(`/medium-features/decomposition/preparations/${prepMatch.id}/unit`, {
            newUnit: normNew,
            businessId,
            ...(rate !== undefined ? { factor: rate } : {}),
          });
          if (!result?.success) throw new Error(result?.error || 'Failed to change preparation unit.');
          notify(`Preparation "${currentName}" unit changed to ${normNew}.`, 'success');
          closeModal();
          // Same payload shape as an item unit change — a preparation appears as an
          // ingredient row too, so the host patches it the same way.
          if (onChanged) onChanged({ mode: 'unit', itemName: currentName, newUnit: normNew });
          return;
        }

        const crossFamily = isCrossFamilyChange(normCurrent, normNew);
        if (crossFamily) {
          const match = findItemByName(currentName);
          if (!match) { setError('Item not found.'); setLoading(false); return; }
          let currentUnitId = match.standard_unit_id;
          if (!currentUnitId) {
            const found = Object.keys(UNIT_ID_MAP).find((id) => UNIT_ID_MAP[id] === normCurrent);
            currentUnitId = found ? parseInt(found, 10) : null;
          }
          const newIdKey = Object.keys(UNIT_ID_MAP).find((id) => UNIT_ID_MAP[id] === normNew);
          setCrossFamilyData({
            itemId: match.id,
            itemName: currentName,
            currentStock: 0,
            currentUnit: normCurrent,
            currentUnitId,
            newUnit: normNew,
            newUnitId: newIdKey ? parseInt(newIdKey, 10) : null,
          });
          closeModal();
          setShowCrossFamily(true);
          return;
        }

        // Same-family: apply unit change directly
        try {
          const result = await UnitMappingService.confirmUnitChange(
            currentName, normCurrent, normNew,
            undefined, 'Changed via Item Mapping'
          );
          if (result && result.success) {
            notify('Unit changed successfully. Quantities updated.', 'success');
            closeModal();
            if (onChanged) onChanged({ mode: 'unit', itemName: currentName, newUnit: normNew });
          } else {
            throw new Error(result.error || 'Failed to change unit.');
          }
        } catch (err) {
          setError(err.message || 'Failed to change unit.');
        }
        return;
      }
    } catch (e) {
      setError(e?.message || 'Failed to update item.');
    } finally {
      setLoading(false);
    }
  }, [mode, form, confirmStep, findItemByName, findPrepByName, selectedPrep, mergeTargetPrep, businessId, notify, closeModal, onChanged]);

  // ── Unit-change confirmation modal handlers (same-family) ──────────────────
  const handleUnitConfirmApply = useCallback(async (conversionFactor) => {
    if (!pendingUnit) return;
    setApplyingUnit(true);
    try {
      const result = await UnitMappingService.confirmUnitChange(
        pendingUnit.itemName, pendingUnit.currentUnit, pendingUnit.newUnit,
        conversionFactor, 'Changed via Item Mapping',
      );
      if (result.success) {
        notify('Unit changed successfully. Quantities updated.', 'success');
        setShowUnitConfirm(false);
        setPendingUnit(null);
        setUnitPreview(null);
        if (onChanged) onChanged({ mode: 'unit', itemName: pendingUnit.itemName, newUnit: pendingUnit.newUnit });
      } else {
        notify(result.error || 'Failed to apply unit change', 'error');
      }
    } catch (e) {
      notify(e?.message || 'Failed to apply unit change', 'error');
    } finally {
      setApplyingUnit(false);
    }
  }, [pendingUnit, notify, onChanged]);

  // ── Labels ────────────────────────────────────────────────────────────────
  const title = mode === 'rename' ? 'Change item name'
    : mode === 'merge' ? 'Merge items'
    : mode === 'unit' ? 'Change item unit' : 'Edit item';
  const description = mode === 'rename' ? 'Update the item name across inventory and recipes.'
    : mode === 'merge' ? 'Move all stock and recipes into another item — or pick a saved preparation to make this item from.'
    : mode === 'unit' ? 'Change the unit used for this item.' : '';
  const primaryLabel = loading
    ? (mode === 'merge' ? 'Merging...' : 'Saving...')
    : mode === 'rename' ? 'Rename item'
    : mode === 'merge' ? 'Merge items'
    : mode === 'unit' ? 'Change unit' : 'Save';

  // Whether "Merge into" is currently naming a preparation rather than an item — the confirm
  // step describes a different operation in that case, so it must not say "merge".
  const mergeTargetIsPreparation = mode === 'merge' && !!(
    mergeTargetPrep || (!findItemByName(form.mergeTargetName) && findPrepByName(form.mergeTargetName))
  );

  const normCurrentForCheck = UnitMappingService.normalizeUnitSymbol(form.currentUnit || '');
  const isSameUnit = form.newUnit && normCurrentForCheck === UnitMappingService.normalizeUnitSymbol(form.newUnit);

  const usagePanel = (
    <div style={{ minHeight: '110px', border: '1px solid #eef2f7', borderRadius: '14px', background: '#fff', padding: '12px 14px', marginBottom: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '10px', textAlign: 'left' }}>
        {isMobile ? 'Related items are affected' : 'Related items affected by this ingredient'}
      </div>
      {usageLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#6b7280', fontSize: '14px', minHeight: '48px', justifyContent: 'center' }}>
          Loading related recipes and preparations...
        </div>
      ) : usageError ? (
        <div style={{ fontSize: '14px', color: '#b45309', textAlign: 'left' }}>{usageError}</div>
      ) : usage && (usage.menuItems.length > 0 || usage.preparations.length > 0 || (usage.complimentaryItems || []).length > 0) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
          {usage.menuItems.length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>Menu items</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 150, overflowY: 'auto' }}>
                {usage.menuItems.map((it, i) => (
                  <li key={`m-${i}`} style={{ padding: '7px 6px', fontSize: '13px', color: '#333', display: 'flex', gap: '8px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 18, textAlign: 'right' }}>{i + 1}.</span>
                    <span style={{ flex: 1 }}>{typeof it === 'object' ? it.name : it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {usage.preparations.length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>Preparations</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 150, overflowY: 'auto' }}>
                {usage.preparations.map((name, i) => (
                  <li key={`p-${i}`} style={{ padding: '7px 6px', fontSize: '13px', color: '#333', display: 'flex', gap: '8px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 18, textAlign: 'right' }}>{i + 1}.</span>
                    <span style={{ flex: 1 }}>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(usage.complimentaryItems || []).length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>Complimentary items</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 150, overflowY: 'auto' }}>
                {usage.complimentaryItems.map((it, i) => (
                  <li key={`c-${it.id}`} style={{ padding: '7px 6px', fontSize: '13px', color: '#333', display: 'flex', gap: '8px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 18, textAlign: 'right' }}>{i + 1}.</span>
                    <span style={{ flex: 1 }}>{it.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'left' }}>
          No linked recipes, preparations, or complimentary items were found for this item.
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Pencil button + 3-option menu */}
      <div className="overview-edit-actions" ref={menuWrapRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className={buttonClassName || 'overview-edit-btn'}
          onClick={() => setShowMenu((p) => !p)}
          title={buttonTitle}
          aria-label={buttonTitle}
          style={buttonStyle}
        >
          <FontAwesomeIcon icon={faPen} style={{ fontSize: 12 }} />
        </button>
        {showMenu && (
          <div className="overview-edit-menu">
            <button type="button" className="overview-edit-option" onClick={() => openModal('rename')}>Change item name</button>
            <button type="button" className="overview-edit-option" onClick={() => openModal('merge')}>Merge with other item</button>
            <button type="button" className="overview-edit-option" onClick={() => openModal('unit')}>Change units</button>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {mode && ReactDOM.createPortal(
        <div className="modal-overlay inventory-edit-overlay">
          <div className="modal-box inventory-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inventory-edit-header">
              <h3 className="inventory-edit-title">{title}</h3>
              <button type="button" className="inventory-edit-close" onClick={closeModal} disabled={loading} aria-label="Close edit dialog">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {description && <p className="inventory-edit-subtitle">{description}</p>}
            <form className="inventory-edit-form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              <div className="inventory-edit-field">
                <label className="inventory-edit-label">Current item name</label>
                <input
                  className="inventory-edit-input"
                  type="text"
                  value={form.currentName}
                  onChange={(e) => setField('currentName', e.target.value)}
                  onFocus={() => setShowNameSug(true)}
                  onBlur={() => setTimeout(() => setShowNameSug(false), 120)}
                  placeholder="e.g. Apricot"
                  autoComplete="off"
                  autoFocus
                  disabled={loading}
                />
                {showNameSug && nameSuggestions.length > 0 && (
                  <div className="inventory-edit-suggestions">
                    {nameSuggestions.map((entry) => (
                      <button key={entry.label} type="button" className="inventory-edit-suggestion"
                        onMouseDown={(e) => { e.preventDefault(); pickCurrentName(entry); }}>
                        {entry.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {mode === 'rename' && (
                <div className="inventory-edit-field">
                  <label className="inventory-edit-label">New item name</label>
                  <input className="inventory-edit-input" type="text" value={form.newName}
                    onChange={(e) => setField('newName', e.target.value)} placeholder="Enter new item name"
                    autoComplete="off" disabled={loading} />
                </div>
              )}

              {mode === 'merge' && (
                <div className="inventory-edit-field">
                  <label className="inventory-edit-label">Merge into item or preparation</label>
                  <input className="inventory-edit-input" type="text" value={form.mergeTargetName}
                    onChange={(e) => { setMergeTargetPrep(null); setField('mergeTargetName', e.target.value); }}
                    onFocus={() => setShowMergeSug(true)}
                    onBlur={() => setTimeout(() => setShowMergeSug(false), 120)}
                    placeholder="Select target item" autoComplete="off" disabled={loading} />
                  {showMergeSug && mergeSuggestions.length > 0 && (
                    <div className="inventory-edit-suggestions">
                      {mergeSuggestions.map((entry) => (
                        <button key={entry.label} type="button" className="inventory-edit-suggestion"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            // Remember WHICH row was clicked. An item and a preparation can share
                            // a name, and the two do very different things.
                            setMergeTargetPrep(entry.prep);
                            setField('mergeTargetName', entry.name);
                            setShowMergeSug(false);
                          }}>
                          {entry.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {mode === 'unit' && (
                <>
                  <div className="inventory-edit-field" style={{ position: 'relative' }}>
                    <label className="inventory-edit-label">Current unit</label>
                    <div tabIndex={0} role="button" aria-haspopup="listbox" aria-expanded={showCurrentUnitDrop}
                      className="inventory-edit-input"
                      onClick={() => setShowCurrentUnitDrop((p) => !p)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowCurrentUnitDrop((p) => !p); } }}
                      onBlur={() => setTimeout(() => setShowCurrentUnitDrop(false), 120)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                      <span style={{ color: form.currentUnit ? '#111827' : '#9ca3af' }}>
                        {currentUnitOptions.find((u) => u.value === form.currentUnit)?.label || form.currentUnit || 'Select unit'}
                      </span>
                      <span style={{ marginLeft: 8, color: '#6b7280' }}>▾</span>
                    </div>
                    {showCurrentUnitDrop && (
                      <div className="inventory-edit-suggestions" style={{ position: 'absolute', left: 0, right: 0, zIndex: 1200, bottom: 'calc(100% + 6px)' }}>
                        {currentUnitOptions.map((u) => (
                          <button key={u.value} type="button" className="inventory-edit-suggestion"
                            onMouseDown={(e) => { e.preventDefault(); setField('currentUnit', u.value); setShowCurrentUnitDrop(false); }}>
                            {u.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="inventory-edit-field" style={{ position: 'relative' }}>
                    <label className="inventory-edit-label">New unit</label>
                    <div tabIndex={0} role="button" aria-haspopup="listbox" aria-expanded={showUnitDrop}
                      className="inventory-edit-input"
                      onClick={() => setShowUnitDrop((p) => !p)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowUnitDrop((p) => !p); } }}
                      onBlur={() => setTimeout(() => setShowUnitDrop(false), 120)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                      <span style={{ color: form.newUnit ? '#111827' : '#9ca3af' }}>
                        {filteredUnitOptions.find((u) => u.value === form.newUnit)?.label || 'Select unit'}
                      </span>
                      <span style={{ marginLeft: 8, color: '#6b7280' }}>▾</span>
                    </div>
                    {showUnitDrop && (
                      <div className="inventory-edit-suggestions" style={{ position: 'absolute', left: 0, right: 0, zIndex: 1200, bottom: 'calc(100% + 6px)' }}>
                        {filteredUnitOptions.map((u) => (
                          <button key={u.value} type="button" className="inventory-edit-suggestion"
                            onMouseDown={(e) => { e.preventDefault(); setField('newUnit', u.value); setShowUnitDrop(false); }}>
                            {u.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {isSameUnit && (
                    <div style={{ marginTop: 4, padding: '10px 14px', borderRadius: 10, background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', fontSize: 13, fontWeight: 500 }}>
                      ⚠️ Source and destination units are the same — please select a different unit.
                    </div>
                  )}
                </>
              )}

              {error && <div className="inventory-edit-error">{error}</div>}

              <div className="inventory-edit-actions">
                <button type="button" className="inventory-edit-secondary-btn" onClick={closeModal} disabled={loading}>Cancel</button>
                <button type="submit" className="inventory-edit-primary-btn" disabled={loading || (mode === 'unit' && !!isSameUnit)}>
                  {primaryLabel}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )}

      {/* Confirm step (with related-items usage panel) */}
      {mode && confirmStep && ReactDOM.createPortal(
        <div className="modal-overlay inventory-edit-overlay inventory-edit-confirm-overlay"
          onClick={() => { if (!loading) setConfirmStep(false); }} style={{ zIndex: 10000 }}>
          <div className="modal-box inventory-edit-confirm-modal" onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(92vw, 520px)', background: '#fff', borderRadius: 18, boxShadow: '0 24px 70px rgba(15,23,42,0.28)', padding: '22px 22px 18px', fontFamily: 'Roboto, sans-serif' }}>
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>
                {mode === 'merge'
                  ? (mergeTargetIsPreparation ? 'Confirm preparation' : 'Confirm merge')
                  : mode === 'unit' ? 'Confirm unit change' : 'Confirm rename'}
              </h3>
              {mode === 'merge' && mergeTargetIsPreparation && (
                <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.5, color: '#6b7280' }}>
                  Nothing is deleted. Every recipe keeps pointing at &quot;{form.currentName}&quot;, and sales will
                  deduct what &quot;{form.mergeTargetName}&quot; is made of instead of its own stock.
                </p>
              )}
            </div>
            <div style={{ marginBottom: 14, padding: '8px 0', textAlign: 'center' }}>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</div>
                  <div style={{ fontSize: 14, color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>
                    {mode === 'unit' ? (form.currentUnit || '-') : (form.currentName || '-')}
                  </div>
                </div>
                <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {mode === 'merge' ? 'Merge into' : mode === 'unit' ? 'New unit' : 'New'}
                  </div>
                  <div style={{ fontSize: 14, color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>
                    {mode === 'rename' ? (form.newName || '-') : mode === 'merge' ? (form.mergeTargetName || '-') : (form.newUnit || '-')}
                  </div>
                </div>
              </div>
            </div>

            {usagePanel}

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => setConfirmStep(false)} disabled={loading}
                style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                Back
              </button>
              <button type="button" onClick={handleSubmit} disabled={loading}
                style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: 'none', background: loading ? '#9ca3af' : '#111827', color: '#fff', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? (mode === 'merge' ? (mergeTargetIsPreparation ? 'Applying...' : 'Merging...') : mode === 'unit' ? 'Changing...' : 'Renaming...')
                  : (mode === 'merge'
                    ? (mergeTargetIsPreparation ? 'Use this preparation' : 'Confirm Merge')
                    : mode === 'unit' ? 'Confirm Change' : 'Confirm Rename')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Same-family unit change confirmation */}
      {showUnitConfirm && unitPreview && (
        <UnitChangeConfirmationModal
          isOpen={showUnitConfirm}
          previewData={unitPreview}
          onConfirm={handleUnitConfirmApply}
          onCancel={() => { setShowUnitConfirm(false); setPendingUnit(null); setUnitPreview(null); }}
          isLoading={applyingUnit}
        />
      )}

      {/* Cross-family conversion */}
      {showCrossFamily && crossFamilyData && (
        <CrossFamilyConversionModal
          isOpen={showCrossFamily}
          itemId={crossFamilyData.itemId}
          itemName={crossFamilyData.itemName}
          currentStock={crossFamilyData.currentStock}
          currentUnit={crossFamilyData.currentUnit}
          currentUnitId={crossFamilyData.currentUnitId}
          newUnit={crossFamilyData.newUnit}
          newUnitId={crossFamilyData.newUnitId}
          onConfirm={() => {
            setShowCrossFamily(false);
            setCrossFamilyData(null);
            notify('Unit changed successfully.', 'success');
            if (onChanged) onChanged({ mode: 'unit', itemName: crossFamilyData.itemName, newUnit: crossFamilyData.newUnit });
          }}
          onCancel={() => { setShowCrossFamily(false); setCrossFamilyData(null); }}
        />
      )}
    </>
  );
};

export default InventoryItemEditMenu;
